// src/components/dashboard/MyCourses.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import CourseList from '../courses/CourseList';
import SessionList from '../sessions/SessionList';

// Helper functions with i18n support (kept for backward compatibility)
function formatDate(dateString, locale = 'en') {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString(locale === 'nl' ? 'nl-NL' : 'en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
}

function formatTime(dateStr) {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
}

function formatTimeAgo(time, t) {
  if (!time) return '';
  const diff = new Date().getTime() - new Date(time).getTime();
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 1) return t('common.time.daysAgo', { count: days });
  if (days === 1) return t('common.time.oneDayAgo');
  if (hours > 0) return t('common.time.hoursMinutesAgo', { hours, minutes: minutes % 60 });
  if (minutes > 0) return t('common.time.minutesAgo', { count: minutes });
  return t('common.time.justNow');
}

// Pagination Component with i18n
function Pagination({ currentPage, totalPages, onPageChange }) {
  const { t } = useTranslation();
  
  return (
    <div className="flex justify-center items-center gap-2 sm:gap-4 mt-6 sm:mt-8">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="flex items-center gap-1 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-slate-100 text-slate-600 text-sm transition disabled:opacity-50 disabled:hover:bg-transparent"
      >
        <ChevronLeftIcon className="w-3 h-3 sm:w-4 sm:h-4" />
        <span className="hidden sm:inline">{t('common.pagination.previous')}</span>
      </button>
      <span className="text-xs sm:text-sm text-slate-600">
        {t('common.pagination.pageOf', { current: currentPage, total: totalPages })}
      </span>
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="flex items-center gap-1 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-slate-100 text-slate-600 text-sm transition disabled:opacity-50 disabled:hover:bg-transparent"
      >
        <span className="hidden sm:inline">{t('common.pagination.next')}</span>
        <ChevronLeftIcon className="w-3 h-3 sm:w-4 sm:h-4 rotate-180" />
      </button>
    </div>
  );
}

// Main MyCourses Component - Router between CourseList and SessionList
function MyCourses({
  onCourseSelect,
  selectedCourseId,
  selectedCourseName,
  onBackToCourses,
  onJoinSession,
  user,
  userRole
}) {
  // If a course is selected, show SessionList, otherwise show CourseList
  if (selectedCourseId) {
    return (
      <SessionList
        selectedCourseId={selectedCourseId}
        selectedCourseName={selectedCourseName}
        onBackToCourses={onBackToCourses}
        onJoinSession={onJoinSession}
        user={user}
        userRole={userRole}
      />
    );
  }

  return (
    <CourseList
      onCourseSelect={onCourseSelect}
      user={user}
      userRole={userRole}
    />
  );
}

// Export helper functions for backward compatibility
export { Pagination, formatTimeAgo, formatDate, formatTime };
export default MyCourses;